/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * NOTE:
 * Feel free to use this SearchHostContext and copy-paste it to start off your own HostContext.
 * Please take note of what YOU need to implement.
 */

/* eslint-disable no-unused-vars */
import getConfig from '../../../config';
import {getEnvironment} from '../../../utils/Environment';
import {SEARCH_TABS_CONSTANTS} from './SearchConstants';
import AnalyticsUtil from '../../../utils/AnalyticsUtil';
import platform from 'platform';
import {sapCodeToProductMetaData} from 'cchome-apps/src/constants/appsConstants';
const UrlParse = require('url-parse');

/* eslint-enable no-unused-vars */
// declare interface HostContext {}

declare type HostInfo = {
	surface: string;
	client: string;
	version: any;
	apiKey: string; // ie. 'CCHomeWeb1'
	imsClientId: string; // IMS Client ID
};

class SearchHostContext {
	searchFeatureFlags: Array<string>;
	hostUIState: {
		tab: string;
		folder: string;
		library: string;
	};
	subscriptions: Array<any>;
	/**
	 * Gets the environment of the host. ('stage', 'prod')
	 * @returns {string}
	 */
	get environment() {
		return getEnvironment() === 'prod' ? 'prod' : 'stage';
	}

	/**
	 * Gets the locale of the host.
	 * @returns {string}
	 */
	get locale() {
		return window.cchome.util.getLocale();
	}

	/**
	 * DEPRECATED: Gets the hostInfo of the host.
	 * @returns {HostInfo}
	 */
	get clientInfo() {
		return this.getHostInfo();
	}

	/**
	 * Gets the hostInfo of the host.
	 * @returns {HostInfo}
	 */
	get hostInfo(): HostInfo {
		return this.getHostInfo();
	}

	/**
	 * Gets the logged out info of the user in the host.
	 * @returns {bool}
	 */
	get userLoggedOut() {
		return false;
	}

	/**
	 * Gets the imsData object of the host.
	 * @returns {IMSInfo}
	 */
	get imsInfo() {
		return this.getImsInfo();
	}

	/**
	 * Returns an array of the result types that you, the host, will handle in terms of onclicks.
	 * @returns {Array<string>}
	 */
	get handledResultCategories() {
		// CCHome Web will handle the onclick actions of these result types
		return [
			'apps',
			'lightroom',
			'ccFiles',
			'mobileCreations',
			'spark',
			'cloudDocs',
			'libraries',
			'xdArtifacts',
			'localFiles',
		];
	}

	/**
	 * Gets the UI styling needed from the host to display the SRP properly.
	 * @returns {HostUIStyling}
	 */
	get uiStyling() {
		return {
			topOffsetHeight: 64, // topnav height
			bottomOffsetHeight: 60, // footer height, 47 at thinnest, 84 at highest...the height of the footer changes based on breakpoint
		};
	}

	/**
	 * Gets the feature flags from the host.
	 * @returns {Array<string>}
	 */
	get featureFlags() {
		return this.searchFeatureFlags;
	}

	/**
	 * Returns whether or not SRP should display tutorials as a modal or open them in a new tab.
	 * @returns {boolean}
	 */
	get showTutorialModal() {
		return true;
	}

	/**
	 * Returns the default tab that the host wants to land on inside SRP. If none specified, falls back to a predetermined tab from the server config.
	 * @returns {TabName}
	 */
	get searchDefaultTab() {
		return this.defaultTab;
	}

	/**
	 * Gets the HostUIState.
	 */
	getHostUIState() {
		return this.hostUIState;
	}

	/**
	 * @param {Query} query
	 * @param {StartIndexType} startIndex
	 * @param {number} limit
	 * @returns {Promise<SearchResult>}
	 */
	search(query: string, startIndex: number, limit: number): Promise<any> {
		// eslint-disable-line no-unused-vars
		return new Promise((resolve) => {
			resolve({
				resultItems: [],
				totalResults: 0,
			});
		});
	}

	/**
	 * NOTE: This method will get called inside Search component whenever handling a result that the host has specified in handledResultCategories.
	 * For all other categories, we open the link using your openURI.
	 * @param {ClickResult} clickResult
	 */
	handleResultClick(clickResult) {
		if (clickResult) {
			window.cchome.util.logger.log('SearchHostContext', 'clickResult', clickResult);

			const action = clickResult.action;
			switch (action) {
				case 'open':
				case 'openIn':
					this.handleResultClickOpen(clickResult);
					break;
				case 'locate':
					this.handleResultClickLocate(clickResult);
					break;
				default:
					window.cchome.util.logger.log(
						'Search Results Page',
						'Unrecognized action type in onClickCard handler: ',
						action,
					);
			}
		}
		else {
			window.cchome.util.logger.log('Search Results Page', 'No clickResult supplied in onClickCard handler');
		}
	}

	/**
	 * Handle open click events
	 * @param {ClickResult} clickResult
	 */
	handleResultClickOpen(clickResult) {
		const clickResultType = clickResult.item.attributes.assetCategory;
		const clickResultOpenUrl = clickResult.item.attributes.openUrl;
		switch (clickResultType) {
			case 'lightroom':
			case 'spark':
				this.openURI(clickResultOpenUrl);
				break;
			case 'cloudDocs':
			case 'ccFiles':
			case 'libraries':
			case 'mobileCreations':
			case 'xdArtifacts':
				this.openURISameTab(clickResultOpenUrl);
				break;
			case 'apps':
				this.openAppsResult(clickResult);
				break;
			default:
				window.cchome.util.logger.log(
					'Search Results Page',
					'Unrecognized result type in onClickCard open handler',
					clickResultType,
				);
		}
	}

	/**
	 * Handle locate click events
	 * @param {ClickResult} clickResult
	 */
	handleResultClickLocate(clickResult) {
		const clickResultType = clickResult.item.attributes.assetCategory;
		const clickResultParentUrl = clickResult.item.attributes.parentUrl;

		switch (clickResultType) {
			case 'cloudDocs':
			case 'ccFiles': {
				this.openURI(clickResultParentUrl, true);
				break;
			}
			case 'libraries':
				if (clickResult.item.assetType === 'libraryElement') {
					this.openURI(clickResultParentUrl, true);
				}

				break;
			case 'lightroom':
			case 'spark':
			case 'mobileCreations':
			case 'xdArtifacts':
			case 'apps':
				window.cchome.util.logger.log(
					'Search Results Page',
					'At this time, we are not handling onClickCard locate for ',
					clickResultType,
				);
				break;
			default:
				window.cchome.util.logger.log(
					'Search Results Page',
					'Unrecognized result type in onClickCard locate handler',
					clickResultType,
				);
		}
	}

	/**
	 * IMPORTANT! This is the onClick handler for ALL non-handledResultCategories related results.
	 * IMPORTANT! You MUST have this defined, however you, as the host, opens links. It must take in a valid link and open it in a web browser (however way you wish).
	 * Opens the given URL in a browser window/tab (however you'd like to implement that).
	 * @returns {Promise<boolean>}
	 */
	openURI(uri: string, sameTab?: boolean) {
		if (sameTab) {
			this.openURISameTab(uri);
		}
		else {
			window.open(uri, '_blank');
		}
		return true;
	}

	/**
	 * Opens the given URL in the same tab (however you'd like to implement that).
	 * @returns {Promise<boolean>}
	 */
	openURISameTab(uri: string) {
		if (uri) {
			/**
			 * CCH-3590 - XD uses referrer to resolve back button behavior. IE does not update the
			 * referrer data if JS opens a new window. For this to work correctly, we are adding this
			 * as a workaround.
			 *
			 * window.open(resultObject.openUrl, '_self');
			 */
			const link = document.createElement('a');
			const body = document.body;

			const linkHref = uri;
			const linkHrefUrl = new UrlParse(linkHref, true);
			// Get the queries of the openUrl.
			const linkHrefUrlQueries = linkHrefUrl.query;

			// Get the queries and set the 'fromsearch' query.
			linkHrefUrlQueries.fromsearch = true;
			linkHrefUrl.set('query', linkHrefUrlQueries);

			window.cchome.util.logger.log('Search Results Page', 'Click No Tab Url', linkHrefUrl);

			if (link && body) {
				link.href = linkHrefUrl.href;
				link.target = '_self';
				body.appendChild(link);
				link.click();
			}
		}
	}

	/**
	 * Sets the HostUIState.
	 * @param {string} tab
	 * @param {string} folder
	 * @param {string} library
	 */
	setHostUIState(tab: string, folder: string, library: string) {
		this.hostUIState = {
			tab,
			folder,
			library,
		};
	}

	/**
	 * Sets the default tab to land on inside Search.
	 * @returns {void}
	 */
	setSearchDefaultTab(searchDefaultTab: string) {
		this.defaultTab = searchDefaultTab;
	}

	/**
	 * Sets the array of feature flags to trigger inside Search.
	 * @param {void}
	 */
	setFeatureFlags(featureFlags: Array<string>) {
		this.searchFeatureFlags = featureFlags;
	}

	analyticsDataFunction() {
		const userAgent = navigator.userAgent;

		// For CChome return Ingest config
		// information.
		const source = 'CCHome';
		const path = '/ingest/?api_key=cchome-web-service';

		const hostNameStage = 'cc-api-data-stage.adobe.io';
		const hostNameProd = 'cc-api-data.adobe.io';

		const useLocalReferrer = true;
		const referrerStorageName = 'event_referrer';

		const version = '1.0'; // need to get version
		const platformType = 'Web';
		const device = window.navigator.platform;
		const offline = false;
		const category = 'WEB';
		const osVersion = platform.os.version;

		const componentName = 'CCHome';
		const componentVersion = '1.0.0';

		let optOutAnalytics = false;

		let searchPrompt = '';

		if (window.cchome && window.cchome.util && window.cchome.util.optOutAnalytics) {
			optOutAnalytics = window.cchome.util.optOutAnalytics;
		}

		if (AnalyticsUtil.searchPromptId) {
			searchPrompt = AnalyticsUtil.searchPromptId;
		}

		// Init analytics data
		const clientAnalyticsData = {
			analyticsTests: {},
			source,
			path,
			hostNameStage,
			hostNameProd,
			useLocalReferrer,
			referrerStorageName,
			userAgent,
			version,
			platformType,
			device,
			offline,
			category,
			osVersion,
			componentName,
			componentVersion,
			experiment: AnalyticsUtil.experiment,
			optOutAnalytics,
			searchPromptId: searchPrompt,
		};

		// Return client data to Search component
		return {
			analyticData: clientAnalyticsData,
		};
	}

	setSubscriptionEntitlements(subscriptionInfo: SubscriptionInfoType, productEntitlements: Object) {
		this.subscriptions = [
			{
				licensingStatus: subscriptionInfo.status,
				customerSegment: subscriptionInfo.customerSegment,
				planCode: subscriptionInfo.planCode,
				stakeholder: subscriptionInfo.stakeholder,
				productEntitlements,
			},
		];
	}

	setHistory(history: any) {
		this.history = history;
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	static instance: any;
	history: { goBack: Function; push: Function };
	defaultTab: string;

	constructor() {
		if (!SearchHostContext.instance) {
			SearchHostContext.instance = this;
		}
		return SearchHostContext.instance;
	}

	/**
	 * Compiles the host information such as surface, client, and version.
	 * @returns {HostInfo}
	 */
	getHostInfo(): any {
		let hostInfo = {
			surface: 'CCWeb',
			client: 'CCHome',
			version: {major: '1', minor: '0', revision: '0'}, // 1.0.0 is our sample app version
			apiKey: 'CCHomeWeb1',
			imsClientId: 'CCHomeWeb1',
		};

		if (window.cchome.util.hasFeature('enable_custom_host_info_context')) {
			hostInfo = {
				surface: localStorage.getItem('hostContextSurface')
					? (localStorage.getItem('hostContextSurface') as string)
					: 'CCWeb',
				client: localStorage.getItem('hostContextClient')
					? (localStorage.getItem('hostContextClient') as string)
					: 'CCHome',
				version: {major: '1', minor: '0', revision: '0'}, // 1.0.0 is our sample app version
				apiKey: 'CCHomeWeb1',
				imsClientId: 'CCHomeWeb1',
			};
		}
		if (window.cchome.util.hasFeature('enable_space_features')) {
			hostInfo.version.minor = '1'; // To fetch new configuration which will enable space features.
		}
		return hostInfo;
	}

	/**
	 * Compiles the IMS data info for a user. (In sample app, we're assuming the user's logged in and there's a window.adobeIMS object)
	 * @returns {IMSInfo}
	 */
	getImsInfo() {
		let imsInfo = {
			accessToken: '',
			firstName: '',
			lastName: '',
			email: '',
			adobeId: '',
			accountType: '',
			optOutMachineLearning: true,
		};

		const optionalAgreements = window.adobeIMS.getUserProfile()?.optionalAgreements;
		if (optionalAgreements && optionalAgreements.includes('CA_ML')) {
			window.cchome.util.optOutMachineLearning = false;
		}

		if (window.adobeIMS && window.adobeIMS.getAccessToken() && window.adobeIMS.getUserProfile()) {
			let optOutML = true;
			if (window.cchome.util && typeof window.cchome.util.optOutMachineLearning === 'boolean') {
				optOutML = window.cchome.util.optOutMachineLearning;
			}

			const userId = window.cchome.util.getUserId();

			imsInfo = {
				accessToken: window.adobeIMS.getAccessToken(),
				firstName: window.adobeIMS.getUserProfile()?.displayName || '',
				lastName: window.adobeIMS.getUserProfile()?.displayName || '',
				email: window.adobeIMS.getUserProfile()?.email || '',
				adobeId: userId || '',
				accountType: window.adobeIMS.getUserProfile()?.account_type || '',
				optOutMachineLearning: optOutML,
			};
		}
		return imsInfo;
	}

	/**
	 * EXCLUSIVELY FOR CCHOME WEB. Opens the app result item either as a push to history or as a new window.
	 * @param {clickResult}
	 */
	openAppsResult(clickResult: any) {
		// Open the openUrl for Creative Cloud
		if (clickResult && clickResult.item && clickResult.item.id) {
			const sapCode = clickResult.item.id.toUpperCase();
			const productId = sapCodeToProductMetaData[sapCode] && sapCodeToProductMetaData[sapCode].productId;
			if (typeof productId !== 'undefined' && productId.length) {
				const url = `/apps/all/${productId}/overview`;
				this.history.push(url);
			}
		}
	}
}

export default SearchHostContext;
